import React from 'react';
import {
  Table,
  Badge,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Button,
  UncontrolledTooltip,
} from 'reactstrap';
import MagnifyIcon from 'mdi-react/MagnifyIcon';
import GridIcon from 'mdi-react/ViewGridIcon';
import ListIcon from 'mdi-react/ViewListIcon';
import { Link, useHistory } from 'react-router-dom';
import EditOutlineIcon from 'mdi-react/EditOutlineIcon';
import DeleteOutlineIcon from 'mdi-react/DeleteOutlineIcon';
import DotsHorizontalIcon from 'mdi-react/DotsHorizontalIcon';
import ArrowUpIcon from 'mdi-react/ArrowUpIcon';
import ArrowDownIcon from 'mdi-react/ArrowDownIcon';
import CloudDownloadIcon from 'mdi-react/CloudDownloadIcon';
import moment from 'moment';
import { toast } from 'react-toastify';
import skeletonTableLoader from '../../../shared/components/custom/SkeletonLoaders';
import Pagination from '../../../shared/components/pagination/Pagination';
import EmptyStateInfo from '../../../shared/components/custom/EmptyStateInfo';
import t, { partial } from '../../../util/translation/translation';
import { getBaseDate } from '../../../util/date-and-time';
import Api from '../../../util/api';
import config from '../../../config';
import FilterVariantIcon from 'mdi-react/FilterVariantIcon';
import CheckIcon from '../../../shared/components/custom/CheckIcon';
import useConfig from '../../../util/useConfig';
import FormCard from '../../../shared/components/custom/FormCard';
import SquareIconButton from '../../../shared/components/custom/SquareIconButton';

const UNLIMITED_DATE = '9999-12-31T00:00:00.000Z';
const NewsList = ({
  news,
  loading,
  onChangePage,
  handleSearch,
  handleInput,
  handleModal,
  handleSort,
  order,
  setLoadingNews,
  getNews,
  presentationType,
  handlePresentationTypeChange,
  handleListTypeChange,
}) => {
  const history = useHistory();
  const s = partial('shared');
  const p = partial('NewsFormPage');
  const f = partial('NewsForm');
  const config2 = useConfig();
  const features = config2?.featureFlags;

  const renderSort = (cellOrder) => {
    if (order.orderBy !== cellOrder) {
      return undefined;
    }
    return order.desc ? <ArrowDownIcon size={16} /> : <ArrowUpIcon size={16} />;
  };
  const getStatusBadge = ({
    access: { visible },
    publicationDate,
    expirationDate,
  }) => {
    if (!visible) {
      return { color: 'secondary', text: p('draft') };
    }
    if (moment().isBefore(moment(publicationDate))) {
      return { color: 'warning', text: t('ScheduledMessagesPage.Scheduled') };
    }
    if (
      moment().isAfter(moment(publicationDate)) &&
      moment().isBefore(moment(expirationDate))
    ) {
      return { color: 'success', text: p('published') };
    }
    if (moment().isAfter(moment(expirationDate))) {
      return { color: 'danger', text: s('inActive') };
    }
    return { color: 'danger', text: s('somethingWentWrong') };
  };
  const renderNews = () => {
    if (loading) {
      return skeletonTableLoader(4, 8, 70);
    }

    return news.map((n) => {
      const newsType =
        n.type?.toUpperCase() === 'CUSTOM'
          ? s('custom').toUpperCase()
          : n.type?.toUpperCase();
      const status = getStatusBadge(n);

      return (
        <tr key={`news-${n.id}`} className="center-list">
          <td>
            <Link to={`/news/edit/${n.id}`}>{n.title}</Link>
          </td>
          <td>
            <div>{getBaseDate(n.publicationDate)}</div>
            <small className="text-muted">
              {n.expirationDate !== UNLIMITED_DATE
                ? getBaseDate(n.expirationDate)
                : '-'}
            </small>
          </td>
          <td>{newsType || ''}</td>
          {config.clubWebsiteEnabled ||
            (features.featureFlags?.includes('website') && (
              <td className="flex-Hcenter">
                <CheckIcon state={!!n.isFeatured} />
              </td>
            ))}
          <td>{n.categories.length ? n.categories.join(',') : '-'}</td>
          <td className="text-center">
            {n.presses !== undefined ? (
              <div>{n.presses}</div>
            ) : (
              skeletonTableLoader(1, 1, 50)
            )}
            {n.likesEnabled && (
              <small className="text-muted">
                {n.likes || 0} {s('likes')}
              </small>
            )}
          </td>
          <td>
            <Badge color={status.color}>{status.text}</Badge>
          </td>
          <td>
            <div className="icon-actions">
              <SquareIconButton
                icon={<EditOutlineIcon />}
                onClick={() => history.push(`/news/edit/${n.id}`)}
                title={s('edit')}
                id={`edit-${n.id}`}
              />
              <SquareIconButton
                icon={<DeleteOutlineIcon />}
                onClick={() => handleModal(true, n.id, 'news')}
                title={s('delete')}
                tone="danger"
                id={`delete-${n.id}`}
              />
            </div>
          </td>
        </tr>
      );
    });
  };
  const refreshNews = async () => {
    setLoadingNews(true);
    try {
      await Api.news.refreshNews();
      setTimeout(() => getNews(), 3000);
    } catch (err) {
      setLoadingNews(false);
      toast.error(err || p('fetchingNewsFailed'));
    }
  };
  const presentationTypeOptions = [
    { label: s('all'), value: 'all' },
    { label: f('presentationType-news'), value: 'news' },
    { label: f('presentationType-video'), value: 'video' },
    { label: f('presentationType-youtube'), value: 'youtube' },
    { label: f('presentationType-page'), value: 'page' },
    { label: f('presentationType-short'), value: 'short' },
  ];
  const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);
  return (
    <>
      <div className="form-card__switch mb-3">
        <button type="button" className="form-card__switch-btn active">
          <ListIcon size={16} style={{ marginTop: -2, marginRight: 4 }} />
          {capitalize(s('list'))}
        </button>
        <button
          type="button"
          className="form-card__switch-btn"
          onClick={() => handleListTypeChange('grid')}
        >
          <GridIcon size={16} style={{ marginTop: -2, marginRight: 4 }} />
          {capitalize(s('grid'))}
        </button>
      </div>
      <FormCard
        title={s('news')}
        actions={
          <>
            <div
              className="d-none d-md-flex align-items-center"
              style={{ gap: 10 }}
            >
              <div className="icon-actions">
                <UncontrolledDropdown>
                  <DropdownToggle
                    tag="button"
                    type="button"
                    id="news-filter-toggle"
                    className="square-icon-btn"
                    caret={false}
                  >
                    <FilterVariantIcon />
                  </DropdownToggle>
                  <UncontrolledTooltip
                    placement="bottom"
                    target="news-filter-toggle"
                  >
                    {presentationType === 'all'
                      ? s('filter')
                      : f(`presentationType-${presentationType}`)}
                  </UncontrolledTooltip>
                  <DropdownMenu className="dropdown-menu--modern">
                    {presentationTypeOptions.map((option) => (
                      <DropdownItem
                        key={option.value}
                        active={presentationType === option.value}
                        onClick={() => handlePresentationTypeChange(option)}
                      >
                        {option.label}
                      </DropdownItem>
                    ))}
                  </DropdownMenu>
                </UncontrolledDropdown>
                <SquareIconButton
                  icon={<CloudDownloadIcon />}
                  onClick={refreshNews}
                  title={p('fetchNews')}
                  id="news-fetch-btn"
                />
              </div>
              <div className="form noWrap">
                <div className="form__form-group products-list__search">
                  <input
                    placeholder={s('search')}
                    value={news.searchText}
                    onKeyPress={(event) => handleSearch(event.key)}
                    onChange={(event) => handleInput(event.target.value)}
                    name="search"
                    className="rounded"
                  />
                  <MagnifyIcon />
                </div>
              </div>
              <Link to="/news/create">
                <Button size="sm" color="primary" className="mb-0">
                  {s('create')} {s('news')}
                </Button>
              </Link>
            </div>
            <div
              className="d-flex d-md-none align-items-center"
              style={{ gap: 8 }}
            >
              <div className="form noWrap">
                <div className="form__form-group products-list__search">
                  <input
                    placeholder={s('search')}
                    value={news.searchText}
                    onKeyPress={(event) => handleSearch(event.key)}
                    onChange={(event) => handleInput(event.target.value)}
                    name="search"
                    className="rounded"
                  />
                  <MagnifyIcon />
                </div>
              </div>
              <UncontrolledDropdown>
                <DropdownToggle
                  tag="button"
                  type="button"
                  className="square-icon-btn"
                  caret={false}
                >
                  <DotsHorizontalIcon />
                </DropdownToggle>
                <DropdownMenu right className="dropdown-menu--modern">
                  {presentationTypeOptions.map((presentationType) => (
                    <DropdownItem
                      key={`mobile-${presentationType.value}`}
                      onClick={() =>
                        handlePresentationTypeChange(presentationType)
                      }
                    >
                      {presentationType.label}
                    </DropdownItem>
                  ))}
                  <DropdownItem divider />
                  <DropdownItem onClick={refreshNews}>
                    {p('fetchNews')}
                  </DropdownItem>
                  <DropdownItem tag={Link} to="/news/create">
                    {s('create')} {s('news')}
                  </DropdownItem>
                </DropdownMenu>
              </UncontrolledDropdown>
            </div>
          </>
        }
        bodyClassName="form-card__body--flush"
      >
        {!!news && news?.length === 0 && !loading ? (
          <div className="my-3">
            <EmptyStateInfo
              icon="newspaper"
              url="/news/create"
              linkName={p('createNews')}
              title={p('createTitle')}
              text={p('createText')}
            />
          </div>
        ) : (
          <Table responsive striped className="table--roomy">
            <thead>
              <tr>
                <th>
                  <div className="flex alignItems-center">
                    {s('heading')} {renderSort('title')}
                  </div>
                </th>
                <th
                  className="sortable"
                  onClick={() => handleSort('publicationDate')}
                >
                  <div className="flex alignItems-center">
                    {s('publicationDate')} {renderSort('publicationDate')}
                  </div>
                </th>
                <th>
                  <div className="flex alignItems-center">{s('type')}</div>
                </th>
                {config.clubWebsiteEnabled ||
                  (features.featureFlags?.includes('website') && (
                    <th
                      className="sortable"
                      onClick={() => handleSort('isFeatured')}
                    >
                      <div className="flex alignItems-center">
                        {s('featuredNews')} {renderSort('isFeatured')}
                      </div>
                    </th>
                  ))}
                <th>
                  <div className="flex alignItems-center">
                    {s('categories')}
                  </div>
                </th>

                <th className="sortable" onClick={() => handleSort('likes')}>
                  <div className="flex alignItems-center">
                    {s('views')} / {s('likes')} {renderSort('likes')}
                  </div>
                </th>
                <th>{s('status')}</th>
                <th />
              </tr>
            </thead>
            <tbody>{renderNews()}</tbody>
          </Table>
        )}
      </FormCard>
      {(loading || news?.length > 0) && (
        <Pagination
          items={order.total}
          onChangePage={onChangePage}
          initialPage={0}
        />
      )}
    </>
  );
};

export default NewsList;
